import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const AuthContext = createContext(null);


// Configure axios to always include credentials (cookies)
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      setError(null);
      setSessionExpired(false);
    } catch (err) {
      setUser(null);
      // Check for session expired (logged in from another device)
      if (err.response?.data?.detail?.includes('another device')) {
        setSessionExpired(true);
        setError('Your session has expired. You have been logged in from another device.');
      } else if (err.response?.status !== 401) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Set up axios interceptor to catch session expired errors globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.data?.detail?.includes('another device')) {
          setUser(null);
          setSessionExpired(true);
          setError('Your session has expired. You have been logged in from another device.');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      setSessionExpired(false);
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });
      await checkAuth(); // Fetch user data after login
      return { success: true, data: response.data };
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const signup = async (email, password) => {
    try {
      setError(null);
      setSessionExpired(false);
      const response = await axios.post(`${API_URL}/api/auth/signup`, {
        email,
        password
      });
      await checkAuth(); // Fetch user data after signup
      return { success: true, data: response.data };
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Signup failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
      setUser(null);
      setError(null);
      setSessionExpired(false);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const clearSessionExpired = () => {
    setSessionExpired(false);
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    sessionExpired,
    login,
    signup,
    logout,
    checkAuth,
    clearSessionExpired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
